import { CombinedGraphQLErrors } from "@apollo/client/errors"

/** Pull a `errors: string[]` list out of an `errorInfo` object on a GraphQL
 *  error. Returns the joined message, or null if no list is present. */
const errorsFromInfo = (errorInfo: unknown): string | null => {
  if (!errorInfo || typeof errorInfo !== "object") return null
  const errors = (errorInfo as { errors?: unknown }).errors
  if (Array.isArray(errors) && errors.length > 0) {
    const lines = errors.filter((e): e is string => typeof e === "string")
    if (lines.length > 0) return lines.join(" • ")
  }
  return null
}

/**
 * The order-management backend returns structured validation errors under
 * an `errorInfo.errors` (a string[]) array on each GraphQL error. AppSync
 * surfaces this object at the top level of the error (alongside `path` /
 * `message`), but Apollo Link variants sometimes copy it into `extensions`.
 * Look in both spots, and fall back to the GraphQL message.
 */
export const extractWorkflowErrorMessage = (
  error: Error | undefined
): string | null => {
  if (!error) return null

  if (CombinedGraphQLErrors.is(error)) {
    for (const gqlError of error.errors) {
      // AppSync top-level shape: gqlError.errorInfo.errors[]
      const topLevel = errorsFromInfo(
        (gqlError as { errorInfo?: unknown }).errorInfo
      )
      if (topLevel) return topLevel
      // Apollo-normalised shape: gqlError.extensions.errorInfo.errors[]
      const nested = errorsFromInfo(
        (gqlError.extensions as { errorInfo?: unknown })?.errorInfo
      )
      if (nested) return nested
      if (gqlError.message) return gqlError.message
    }
  }

  return error.message || null
}
